import { useCallback, useEffect, useMemo, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { useCorporateBondScreenerSidebarFilterContext } from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';
import type { IDictionaryCountry } from '@/creditflowresearch/domain/vo/IDictionary';
import { toggleMany } from '@/creditflowresearch/application/BondScreener/selection';
import normalizeGroupSelection
  from '@/creditflowresearch/application/BondScreener/normalizeGroupSelection';
import buildRegionTree, { type IRegionNodeWithCounts } from '@/creditflowresearch/application/BondScreener/buildRegionTree';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { getCountryIdsByRegions } from './helpers';

const MARKETS = [
  { id: 'isEmerging' as const, label: 'Emerging Markets' },
  { id: 'isWesternEurope' as const, label: 'Western Europe' },
];

const useRegionsCountriesSection = () => {
  const {
    filters,
    localFilters,
    setLocalFilters,
  } = useCorporateBondScreenerSidebarFilterContext();
  const repository = getContainer().get(DictionaryRepository);
  const [countries, setCountries] = useState<IDictionaryCountry[]>([]);
  const [selected, setSelected] = useState<string[]>(localFilters.selectedCountryIds ?? []);
  const granted = useAccessGrant();
  const { regions: accessibleRegionIds } = useUserPlanContext();

  useEffect(() => {
    const loadDictionary = async (): Promise<void> => {
      setCountries(await repository.getCountries());
    };
    loadDictionary();
  }, []);

  useEffect(() => {
    if (countries.length === 0 || selected.length > 0) return;

    const expandableRegions = localFilters.includedRegionIds.filter((id) =>
      granted || accessibleRegionIds.includes(id),
    );
    const regionCountryIds = expandableRegions.length > 0
      ? getCountryIdsByRegions(countries, new Set(expandableRegions))
      : [];
    const directCountryIds = localFilters.includedCountryIds ?? [];

    if (regionCountryIds.length === 0 && directCountryIds.length === 0) return;

    const countryIds = Array.from(new Set([...regionCountryIds, ...directCountryIds]));
    setSelected(countryIds);
    setLocalFilters((prev) => ({ ...prev, selectedCountryIds: countryIds }));
  }, [countries]);

  const regionItems: IRegionNodeWithCounts[] = useMemo(
    () => buildRegionTree(countries, filters?.regions ?? [], selected, accessibleRegionIds, granted),
    [countries, filters?.regions, selected, accessibleRegionIds, granted],
  );

  const enabledCountryIdsWithData = useMemo(() => new Set(
    regionItems.flatMap((r) => r.enabledIds),
  ), [regionItems]);

  const updateSelection = useCallback((next: string[]) => {
    setSelected(next);

    const enabledSelected = next.filter((id) => enabledCountryIdsWithData.has(id));
    const groups = (filters?.regions ?? []).map((r) => ({ id: r.id, childIds: r.countryIds }));
    const { groupIds, childIds } = normalizeGroupSelection(enabledSelected, groups);

    setLocalFilters((prev) => ({
      ...prev,
      includedRegionIds: groupIds,
      includedCountryIds: childIds,
      selectedCountryIds: next,
    }));
  }, [enabledCountryIdsWithData, filters?.regions, setLocalFilters]);

  const markets = useMemo(() => MARKETS.map((market) => {
    const ids = countries.filter((c) => c[market.id]).map((c) => c.id);
    return {
      ...market,
      ids,
      isActive: ids.length > 0 && ids.every((id) => selected.includes(id)),
    };
  }), [countries, selected]);

  const allChildIds = regionItems.flatMap((r) => r.allIds);
  const isAllSelected = allChildIds.length > 0 && allChildIds.every((id) => selected.includes(id));

  return {
    regionItems,
    selected,
    updateSelection,
    markets,
    isAllSelected,
    isIndeterminate: !isAllSelected && selected.length > 0,
    toggleAll: () => updateSelection(toggleMany(selected, allChildIds)),
  };
};

export default useRegionsCountriesSection;
